import React, { useState } from 'react';

const ContentFeed = ({ content, onUpdate, channels }) => {
  const [selectedContent, setSelectedContent] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterChannel, setFilterChannel] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const filteredContent = content.filter(item => {
    const matchesStatus = filterStatus === 'all' || item.analysis_status === filterStatus;
    const matchesChannel = filterChannel === 'all' || item.channel === filterChannel;
    const matchesSearch = searchTerm === '' || 
      item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.relevant_topics.some(topic => topic.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesStatus && matchesChannel && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'text-green-600 bg-green-100';
      case 'pending': return 'text-orange-600 bg-orange-100';
      case 'analyzing': return 'text-blue-600 bg-blue-100';
      case 'failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'analyzed': return '✅';
      case 'pending': return '⏳';
      case 'analyzing': return '🔄';
      case 'failed': return '❌';
      default: return '❓';
    }
  };

  const getConfidenceColor = (score) => {
    if (score >= 0.8) return 'text-green-600';
    if (score >= 0.6) return 'text-yellow-600';
    if (score >= 0.4) return 'text-orange-600';
    return 'text-red-600';
  };

  const handleViewDetails = (contentItem) => {
    setSelectedContent(contentItem);
  };

  const handleCloseDetails = () => {
    setSelectedContent(null);
  };

  const handleReanalyze = async (contentId) => {
    try {
      // TODO: Call backend API to reanalyze content
      console.log('Reanalyzing content:', contentId);
      
      // Update local state
      const updatedContent = content.map(item => 
        item.id === contentId 
          ? { ...item, analysis_status: 'analyzing' }
          : item
      );
      onUpdate(updatedContent);
      
      // Simulate analysis completion
      setTimeout(() => {
        const finalContent = content.map(item => 
          item.id === contentId 
            ? { ...item, analysis_status: 'analyzed', confidence_score: 0.85 }
            : item
        );
        onUpdate(finalContent);
      }, 3000);
      
    } catch (error) {
      console.error('Error reanalyzing content:', error);
    }
  };

  return (
    <div className="p-6 h-full overflow-y-auto">
      {/* Filters and Search */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search content by title or topics..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="analyzed">Analyzed</option>
            <option value="pending">Pending</option>
            <option value="analyzing">Analyzing</option>
            <option value="failed">Failed</option>
          </select>
          <select
            value={filterChannel}
            onChange={(e) => setFilterChannel(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Channels</option>
            {channels.map(channel => (
              <option key={channel.id} value={channel.name}>{channel.name}</option>
            ))}
          </select>
        </div>
        
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600">
            Showing {filteredContent.length} of {content.length} content items
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setFilterStatus('all');
                setFilterChannel('all');
                setSearchTerm('');
              }}
              className="px-3 py-1 text-sm text-gray-600 hover:text-gray-800"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Content List */}
      <div className="space-y-4">
        {filteredContent.map((item) => (
          <div key={item.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.analysis_status)}`}>
                    <span className="mr-1">{getStatusIcon(item.analysis_status)}</span>
                    {item.analysis_status.charAt(0).toUpperCase() + item.analysis_status.slice(1)}
                  </span>
                  <span className="text-sm text-gray-500">{item.channel}</span>
                  <span className="text-sm text-gray-500">
                    {new Date(item.published_date).toLocaleDateString()}
                  </span>
                </div>
                
                <h3 className="text-lg font-medium text-gray-900 mb-2">{item.title}</h3>
                
                {item.relevant_topics && item.relevant_topics.length > 0 && (
                  <div className="mb-3">
                    <div className="text-sm text-gray-600 mb-1">Relevant Topics:</div>
                    <div className="flex flex-wrap gap-2">
                      {item.relevant_topics.map((topic, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {item.analysis_status === 'analyzed' && (
                  <div className="flex items-center space-x-4 text-sm">
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Confidence:</span>
                      <span className={`font-medium ${getConfidenceColor(item.confidence_score)}`}>
                        {(item.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className="text-gray-600">Type:</span>
                      <span className="font-medium text-gray-900">{item.content_type}</span>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-2 ml-4">
                <button
                  onClick={() => handleViewDetails(item)}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                >
                  View Details
                </button>
                {item.analysis_status === 'failed' && (
                  <button
                    onClick={() => handleReanalyze(item.id)}
                    className="px-3 py-1 text-sm bg-orange-100 text-orange-800 rounded hover:bg-orange-200"
                  >
                    Retry
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {filteredContent.length === 0 && (
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No content found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || filterStatus !== 'all' || filterChannel !== 'all'
                ? 'Try adjusting your filters or search terms.'
                : 'No content has been monitored yet. Run monitoring to get started.'}
            </p>
          </div>
        )}
      </div>

      {/* Content Details Modal */}
      {selectedContent && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Content Details</h3>
                <button
                  onClick={handleCloseDetails}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <p className="mt-1 text-sm text-gray-900">{selectedContent.title}</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Channel</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedContent.channel}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Published</label>
                    <p className="mt-1 text-sm text-gray-900">
                      {new Date(selectedContent.published_date).toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Content Type</label>
                    <p className="mt-1 text-sm text-gray-900">{selectedContent.content_type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Analysis Status</label>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-1 ${getStatusColor(selectedContent.analysis_status)}`}>
                      {getStatusIcon(selectedContent.analysis_status)} {selectedContent.analysis_status}
                    </span>
                  </div>
                </div>
                
                {selectedContent.confidence_score > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Confidence Score</label>
                    <div className="mt-1 flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${getConfidenceColor(selectedContent.confidence_score).replace('text-', 'bg-')}`}
                          style={{ width: `${selectedContent.confidence_score * 100}%` }}
                        ></div>
                      </div>
                      <span className={`text-sm font-medium ${getConfidenceColor(selectedContent.confidence_score)}`}>
                        {(selectedContent.confidence_score * 100).toFixed(0)}%
                      </span>
                    </div>
                  </div>
                )}
                
                {selectedContent.relevant_topics && selectedContent.relevant_topics.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Relevant Topics</label>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {selectedContent.relevant_topics.map((topic, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                        >
                          {topic}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={handleCloseDetails}
                  className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentFeed;
